// src/utils/api.js
// Resolve API base from envs, prefer local when running the site on localhost.
// If neither env is present at build time (common on cPanel/static hosts),
// fall back to your API subdomain so calls never go relative.

const viteBase =
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_URL;

const craBase =
    typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_API_BASE;

// Detect if the app itself is being served from localhost (dev)
const isLocalHost = (() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1';
})();

// Prefer a local API when developing. You can override the port via env.
const localApiFromEnv =
    (typeof import.meta !== 'undefined' &&
        import.meta.env &&
        import.meta.env.VITE_LOCAL_API) ||
    (typeof process !== 'undefined' &&
        process.env &&
        process.env.REACT_APP_LOCAL_API);

const LOCAL_API = (localApiFromEnv || 'http://localhost:8080').replace(/\/+$/, '');

// 🔒 Production fallback is your new first-party API subdomain:
const fallbackBase = 'https://api.piedmontalabamamuseum.com';

// Compute and normalize the final base URL
export const API_BASE = String(
    viteBase || craBase || (isLocalHost ? LOCAL_API : fallbackBase)
).replace(/\/+$/, '');

/**
 * apiFetch(path, opts)
 * - No 'X-Requested-With' header (preflight-safe)
 * - Defaults to credentials:'omit' (public calls)
 * - Add credentials:'include' per-call where auth cookies are required
 */
export async function apiFetch(path, opts = {}) {
    const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

    // Start from caller headers, but strip preflight troublemakers
    const headers = new Headers(opts.headers || {});
    headers.delete('X-Requested-With');
    headers.delete('x-requested-with');

    return fetch(url, {
        mode: 'cors',
        credentials: opts.credentials ?? 'omit',
        ...opts,
        headers,
    });
}

/* ──────────────────────────────────────────────────────────────────────────
   Admin helpers
────────────────────────────────────────────────────────────────────────── */

export async function adminLogin(arg1, arg2) {
    let username, password;
    if (arg1 && typeof arg1 === 'object') {
        username = arg1.username;
        password = arg1.password;
    } else {
        username = arg1;
        password = arg2;
    }

    const resp = await apiFetch('/api/admin/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || 'Login failed');
    return data;
}

export async function adminLogout() {
    await apiFetch('/api/admin/logout', { method: 'POST', credentials: 'include' });
}

export async function adminMe() {
    const resp = await apiFetch('/api/admin/me', { credentials: 'include' });
    if (!resp.ok) return null;
    return resp.json();
}

/* Contacts */
export async function adminContacts() {
    const r = await apiFetch('/api/admin/contacts', { credentials: 'include' });
    return r.json();
}
export async function adminCreateContact(body) {
    return apiFetch('/api/admin/contacts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function adminUpdateContact(id, body) {
    return apiFetch(`/api/admin/contacts/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function adminDeleteContact(id) {
    return apiFetch(`/api/admin/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

/* Visitors – flexible query */
export async function adminVisitors(opts = 'this_week') {
    // Backward compatible: adminVisitors('this_week')
    let qs = '';
    if (typeof opts === 'string') {
        qs = `?range=${encodeURIComponent(opts)}`;
    } else if (opts && typeof opts === 'object') {
        const params = new URLSearchParams();
        if (opts.range) params.set('range', String(opts.range));
        if (opts.start) params.set('start', String(opts.start));
        if (opts.end) params.set('end', String(opts.end));
        if (opts.tab) params.set('tab', String(opts.tab)); // 'upcoming' | 'previous'
        qs = `?${params.toString()}`;
    }
    const r = await apiFetch(`/api/admin/visitors${qs}`, { credentials: 'include' });
    return r.json();
}

/* Volunteers */
export async function adminVolunteers() {
    const r = await apiFetch('/api/admin/volunteers', { credentials: 'include' });
    return r.json();
}
export async function adminUpdateVolunteer(id, body) {
    return apiFetch(`/api/admin/volunteers/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function adminDeleteVolunteer(id) {
    return apiFetch(`/api/admin/volunteers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

/* Members */
export async function adminMembers() {
    const r = await apiFetch('/api/admin/members', { credentials: 'include' });
    return r.json();
}
export async function adminCreateMember(body) {
    return apiFetch('/api/admin/members', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function adminUpdateMember(id, body) {
    return apiFetch(`/api/admin/members/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
export async function adminDeleteMember(id) {
    return apiFetch(`/api/admin/members/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });
}

/* Open House (read-only list) */
export async function adminOpenHouse() {
    const resp = await apiFetch('/api/admin/open-house', { credentials: 'include' });
    // Prefer JSON, but if a proxy or 404 returns HTML, surface it as a helpful error.
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('application/json')) {
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
        return data;
    }
    const text = await resp.text().catch(() => '');
    if (!resp.ok) throw new Error(text || `HTTP ${resp.status}`);
    try { return JSON.parse(text); } catch { return {}; }
}

/* Email – supports optional image attachments */
export async function adminSendEmail({ to, subject, message, files }) {
    if (files && files.length) {
        const fd = new FormData();
        fd.append('to', to || '');
        fd.append('subject', subject || '');
        fd.append('message', message || '');
        for (const f of files) fd.append('attachments', f);
        return apiFetch('/api/admin/email', {
            method: 'POST',
            credentials: 'include',
            body: fd, // no manual Content-Type for FormData
        });
    }
    return apiFetch('/api/admin/email', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, message }),
    });
}

/* Events (admin list) */
export async function adminEvents(tab = 'upcoming') {
    const params = new URLSearchParams();
    if (tab) params.set('tab', tab); // 'upcoming' | 'previous' | ''
    const r = await apiFetch(`/api/admin/events?${params.toString()}`, {
        credentials: 'include',
    });
    return r.json();
}

/* Single event (for edit form, etc.) */
export async function adminGetEvent(id) {
    const r = await apiFetch(`/api/admin/events/${id}`, { credentials: 'include' });
    return r.json();
}

/* Documents (meeting minutes / financial reports) */
export async function adminDocuments(category) {
    const params = new URLSearchParams();
    if (category) params.set('category', category); // 'meeting_minutes' | 'financial_report'
    const r = await apiFetch(`/api/admin/documents?${params.toString()}`, {
        credentials: 'include',
    });
    return r.json();
}

/* News list */
export async function adminNews() {
    const r = await apiFetch('/api/admin/news', { credentials: 'include' });
    return r.json();
}

/* Single news post */
export async function adminGetNewsPost(id) {
    const r = await apiFetch(`/api/admin/news/${id}`, { credentials: 'include' });
    return r.json();
}
